import React, {useContext, useState} from 'react';
import {Link} from 'react-router-dom';
import {ChartContext} from '../../../store/ChartContext';
import InstrumentPicker from './InstrumentPicker';
import GranularityPicker from './GranularityPicker';
import SeriesTypePicker from './SeriesTypePicker';
import VirtualAssistantPanel from './VirtualAssistantPanel';
import {API_URL, BACKEND_API_URL, STRIPE_PORTAL_URL} from '../../../env';
import {AuthContext} from "../../../store/AuthContext";
import {getAuthenticatedUser, getLogOut} from "../../../api/backendApi";
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import Logo from "../../../assets/images/logo.svg";
import {LineChart} from "lucide-react";
import { create_checkout_session } from "../../../api/backendApi";
import { BellRing, BellOff  } from 'lucide-react';
import TagManager from "react-gtm-module";

export const NavbarContainer = props => {
    const [isLoading, setIsLoading] = useState(false);
    const {user, setUser} = useContext(AuthContext);

    const {
        instrument,
        setInstrument,
        instruments,
        granularity,
        setGranularity,
        seriesType,
        setSeriesType,
        virtualAssistantSettings,
        setVirtualAssistantSettings,
        setChatGPTData,
        isSoundEnabled, setIsSoundEnabled 
    } = useContext(ChartContext);

    const toggleSound = () => setIsSoundEnabled(prev => !prev);

    const handleTechnicalAnalysisClick = async () => {
        const ENDPOINT_URL = `${API_URL}/technicalAnalysis/`;
        setIsLoading(true);
        setChatGPTData(null);
        
        TagManager.dataLayer({
            dataLayer: {
                event: 'technical_analysis_requested',
                instrument: instrument.name,
                granularity: granularity.value
            }
        });
        
        try {
            const response = await fetch(ENDPOINT_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    instrument: instrument.name,
                    granularity: granularity.value
                }),
                credentials: "include"
            });

            if (!response.ok)
                throw new Error(`HTTP error! status: ${response.status}`);

            const data = await response.json();
            setChatGPTData(data);
            const responseUser = await getAuthenticatedUser();
            if (responseUser.status === 200) {
                const userData = await responseUser.json();
                setUser(userData);
            }
        } catch (error) {
            console.error('Error sending message (technicalAnalysis):', error);
        } finally {
            setIsLoading(false);
        }
    };

    function Spinner() {
        return (
            <span
                style={{
                    border: "2px solid #ccc",
                    borderTop: "2px solid #1F2939",
                    borderRadius: "50%",
                    width: "16px",
                    height: "16px",
                    animation: "spin 1s linear infinite",
                    display: "inline-block",
                }}
            />
        );
    }

    const handleSignOutClick = async () => {
        try {
            const response = await getLogOut();

            if (response.ok)
                window.location.href = `${BACKEND_API_URL}/login/`;
            else
                console.error('Error during logout:', response.statusText);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    return (
        <>
            <nav className="px-4 py-2 text-white primary-navbar">
                <div className="primary-navbar-content">
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                            <button className="hamburger-button">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                          d="M4 6h16M4 12h16m-7 6h7"></path>
                                </svg>
                            </button>
                        </DropdownMenu.Trigger>
                        <div className="mobile-logo-container">
                            <img src={Logo} alt="Logo" className="mobile-logo"/>
                        </div>
                        <DropdownMenu.Content className="bg-black shadow-md rounded-md p-2 z-50 flex flex-col gap-2">
                            <InstrumentPicker 
                                setInstrument={setInstrument} 
                                currentInstrument={instrument} 
                                instruments={instruments}
                                setVirtualAssistantSettings={setVirtualAssistantSettings}
                                virtualAssistantSettings={virtualAssistantSettings}
                            />
                            <SeriesTypePicker setSeriesType={setSeriesType} currentSeriesType={seriesType}/>
                            <GranularityPicker setGranularity={setGranularity} currentGranularity={granularity}/>
                            <VirtualAssistantPanel 
                                setVirtualAssistantSettings={setVirtualAssistantSettings}
                                virtualAssistantSettings={virtualAssistantSettings}
                                instrument={instrument}
                            />
                            {user?.subscription && (
                                <>
                                    <button
                                        type="button"
                                        id="technicalAnalysis"
                                        onClick={handleTechnicalAnalysisClick}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Spinner/>
                                                <span>Technical Analysis</span>
                                            </>
                                        ) : (
                                            <>
                                                <span
                                                    style={{
                                                        border: "2px solid #ccc",
                                                        borderTop: "2px solid #1F2939",
                                                        borderRadius: "50%",
                                                        width: "16px",
                                                        height: "16px",
                                                        // animation: "spin 1s linear infinite",
                                                        display: "inline-block",
                                                    }}
                                                />
                                                <span>Technical Analysis</span>
                                            </>
                                        )}
                                    </button>
                                    <Link to="/">
                                        <LineChart size={20} style={{ marginRight: '5px' }}/>
                                        Chart
                                    </Link>
                                    <p style={{color: "gray"}}>Plan: {user.subscription.plan_name}</p>
                                    <p style={{color: "gray"}}>Available
                                        analyses: {user.subscription.remaining_analyses}</p>
                                </>
                            )}
                            {!user?.subscription && (
                                <DropdownMenu.Item>
                                    <Link to="/subscriptions" style={{color: "gray"}}>
                                        No subscription - buy
                                    </Link>
                                </DropdownMenu.Item>
                            )}
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>
                    <div className="desktop-menu">
                        <div className="desktop-logo-container">
                            <img src={Logo} alt="Logo"/>
                        </div>
                        <InstrumentPicker 
                            setInstrument={setInstrument} 
                            currentInstrument={instrument} 
                            instruments={instruments}
                            setVirtualAssistantSettings={setVirtualAssistantSettings}
                            virtualAssistantSettings={virtualAssistantSettings}
                        />
                        <SeriesTypePicker setSeriesType={setSeriesType} currentSeriesType={seriesType}/>
                        <GranularityPicker setGranularity={setGranularity} currentGranularity={granularity}/>
                        {user?.subscription && (
                            <VirtualAssistantPanel 
                                setVirtualAssistantSettings={setVirtualAssistantSettings}
                                virtualAssistantSettings={virtualAssistantSettings}
                                instrument={instrument}
                            />
                        )}
                        {user?.subscription && (
                            <>
                                <button
                                    type="button"
                                    id="technicalAnalysis"
                                    onClick={handleTechnicalAnalysisClick}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <Spinner/>
                                            <span>Technical Analysis</span>
                                        </>
                                    ) : (
                                        <>
                                                <span
                                                    style={{
                                                        border: "2px solid #ccc",
                                                        borderTop: "2px solid #1F2939",
                                                        borderRadius: "50%",
                                                        width: "16px",
                                                        height: "16px",
                                                        // animation: "spin 1s linear infinite",
                                                        display: "inline-block",
                                                    }}
                                                />
                                            <span>Technical Analysis</span>
                                        </>
                                    )}
                                </button>
                                <Link to="/">
                                    <LineChart size={20} style={{ marginRight: '5px' }}/>
                                    Chart
                                </Link>
                                { !user.subscription.is_demo_mode && (
                                    <>
                                        <p style={{color: "gray"}}>Plan: {user.subscription.plan_name}</p>
                                        <p style={{color: "gray"}}>Available
                                            analyses: {user.subscription.remaining_analyses}</p>
                                        <a href={STRIPE_PORTAL_URL}>
                                            Manage subscription
                                        </a>
                                        <button
                                            type="button"
                                            id="technicalAnalysis"
                                            onClick={() => create_checkout_session(user.subscription.price_id, "payment")}
                                            disabled={isLoading}
                                        >Buy analyses </button>
                                    </>
                                )}
                                { user.subscription.is_demo_mode && (
                                    <>
                                        <p style={{color: "gray"}}>Plan: Demo</p>
                                        <p style={{color: "gray"}}>Available
                                            analyses: {user.subscription.remaining_analyses}
                                        </p>                                        
                                    </>
                                )}
                            </>
                        )}
                        {!user?.subscription && (
                            <>
                                <Link to="/">
                                    <LineChart size={20} style={{ marginRight: '5px' }}/>
                                    Chart
                                </Link>
                                <Link to="/subscriptions" style={{color: "gray"}}>
                                    No subscription - buy
                                </Link>
                                <a href={STRIPE_PORTAL_URL}>
                                    Manage subscription
                                </a>
                            </>
                        )
                        }
                    </div>
                </div>
                <div className="nav-right-side">
                    <button onClick={toggleSound} className="mx-2">
                        {isSoundEnabled ? <BellRing size={20} /> : <BellOff size={20} />}
                    </button>
                    <button id="signout" type="button" onClick={handleSignOutClick}
                            className="focus:ring-2 focus:ring-offset-2 focus:ring-white rounded-md">Sign Out
                    </button>
                </div>
            </nav>
        </>
    );
};